import { useFormikContext } from "formik";
import React, { ElementType, forwardRef, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import Recaptcha from "react-google-invisible-recaptcha";
import { FormikSubmitButton } from "../FormikSubmitButton";

interface Props {
  name?: string;
  className?: string;
  children?: ReactNode;
  icon?: ElementType;
}

export const RecaptchaSubmitButton = forwardRef(
  ({ children, className, name = "g-recaptcha-response", icon }: Props, ref) => {
    const refRecaptcha = useRef<any>(null);
    const [resolved, setResolved] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [cb, setCb] = useState(false);
    const [loading, setLoading] = useState(false);
    const { dirty, setFieldValue, handleSubmit } = useFormikContext();

    const onClick = useCallback(() => {
      if (resolved) {
        return handleSubmit();
      } else if (loaded) {
        setLoading(true);
        refRecaptcha.current?.callbacks.execute();
      } else {
        setLoading(true);
        setCb(true);
        setFieldValue(name, "");
      }
    }, [refRecaptcha, loaded, resolved, handleSubmit, setCb, name]);

    useEffect(() => {
      if (cb && loaded) {
        refRecaptcha.current?.callbacks.execute();
        setCb(false);
      }
    }, [cb, loaded]);

    useEffect(() => {
      if (!dirty) {
        setCb(false);
        setResolved(false);
        setLoaded(false);
      }
    }, [dirty]);

    const onLoaded = useCallback(() => {
      setLoaded(true);
    }, [setLoaded]);

    const onResolved = useCallback(() => {
      const token = refRecaptcha.current?.callbacks.getResponse();
      setFieldValue(name, token);
      setResolved(true);
      setLoading(false);
      return handleSubmit();
    }, [refRecaptcha, setFieldValue, handleSubmit, name]);

    return (
      <div>
        <FormikSubmitButton onClick={onClick} className={className} ref={ref} icon={icon} loading={loading}>
          {children}
        </FormikSubmitButton>
        {dirty && (
          <Recaptcha
            onLoaded={onLoaded}
            onResolved={onResolved}
            ref={refRecaptcha}
            sitekey={process.env.NX_RECAPTCHA_KEY}
          />
        )}
      </div>
    );
  }
);
